import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../Spinner';
import { FaIcon } from '../FaIcon';
import { ClientDashboardColumnsSetsApi } from '../../api/clientDashboardColumnsSets';
import { usePreviewContext } from '../../context/PreviewContext';
import { usePortalClientId } from '../../hooks/usePortalClientId';
import { DashboardRow } from '../dashboard/rows/DashboardRow';
import { RowWithErrorHandler } from '../dashboard/rows/RowWithErrorHandler';
import { ColumnHeader } from '../dashboard/column_header/ColumnHeader';
import {
    BUSINESS_UNIT_COLUMN,
    FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN,
    FULL_PERIOD_CONVERSIONS_COLUMN,
    GOAL_1_COLUMN,
    GOAL_2_COLUMN,
    getColumnData,
    setCustomMetricLabelsCache,
} from '../dashboard/columns';
import { SORT_DOWN, SORT_UP, sortWithDirection, compareByPosition } from '../../utils/Array.extensions';
import {
    GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, DV360_CHANNEL, MERCADO_ADS_CHANNEL,
    SHEET_CHANNEL, SHOPIFY_CHANNEL, TIENDA_NUBE_CHANNEL, WOOCOMMERCE_CHANNEL, VTEX_CHANNEL,
    ANALYTICS_CHANNEL,
} from '../channels/channels';

const noop = () => {};

// ── Personalización de columnas (orden + ancho), persistida en localStorage ──────
// El cliente puede reordenar (drag de headers) y redimensionar columnas; se guarda
// por (agency_client_id, set_id) en esta computadora y se restaura al volver.
const COLS_STORAGE_PREFIX = 'portal_dash_cols_v1';
const MIN_COLUMN_WIDTH = 80;
// La primera columna (Línea de control) necesita más mínimo: si se achica de más, el
// nombre + el botón de expandir se desbordan a la segunda columna.
const FIRST_COLUMN_MIN_WIDTH = 180;
const FIRST_COLUMN_DEFAULT_WIDTH = 240;
const GRAPHIC_COLUMN_DEFAULT_WIDTH = 240;
const DATA_COLUMN_DEFAULT_WIDTH = 150;

// Columnas con barras por-día → más anchas por default.
const GRAPHIC_COLUMNS = new Set([
    FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN,
    FULL_PERIOD_CONVERSIONS_COLUMN,
    GOAL_1_COLUMN,
    GOAL_2_COLUMN,
]);

function defaultColumnWidth(columnId) {
    if (columnId === BUSINESS_UNIT_COLUMN) return FIRST_COLUMN_DEFAULT_WIDTH;
    if (GRAPHIC_COLUMNS.has(columnId)) return GRAPHIC_COLUMN_DEFAULT_WIDTH;
    return DATA_COLUMN_DEFAULT_WIDTH;
}

function minColumnWidth(columnId) {
    return columnId === BUSINESS_UNIT_COLUMN ? FIRST_COLUMN_MIN_WIDTH : MIN_COLUMN_WIDTH;
}

function colsStorageKey(clientId, setId) {
    return `${COLS_STORAGE_PREFIX}_${clientId}_${setId}`;
}

/**
 * Tablero del Portal del Cliente — V2 formato tabla.
 *
 * Recibe las BUs activas del cliente desde `PortalHome` (que ya las trae de
 * `GET /client/<id>`) y carga del backend el modo + las Plantillas de columnas
 * asignadas + el catálogo de indicators de la agencia.
 *
 * Si modo distinto de TABLE_V2 o sin plantillas → `onFallbackToCards` se ejecuta
 * y el componente no renderiza (PortalHome muestra la V1 de cards).
 *
 * Props:
 *  - businessUnits: array de BUs activas del cliente (shape de `BusinessUnitBasicPresenter`).
 *  - onFallbackToCards(): callback cuando el cliente debe ver el formato V1.
 */
export function PortalDashboardTableV2({ businessUnits, onFallbackToCards }) {
    const { t } = useTranslation();
    // En "Ver como cliente" (preview) el usuario logueado es un agency user, no un
    // cliente: `/portal/me/` exige permiso de cliente → 403 silencioso y el set asignado
    // nunca se muestra. En ese caso leemos los sets del cliente previsualizado vía el
    // endpoint de preview (`/portal/preview/<id>/`, permiso de agencia). Mismo shape.
    const { isPreview, previewClientId } = usePreviewContext();
    // Cliente activo del portal (multi-cliente): scopea el fetch y evita cache cruzado.
    const activeClientId = usePortalClientId();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSetId, setActiveSetId] = useState(null);

    useEffect(() => {
        const request = (isPreview && previewClientId)
            ? ClientDashboardColumnsSetsApi.listPreviewSets(previewClientId)
            : ClientDashboardColumnsSetsApi.listMySets(activeClientId);
        request
            .then((response) => {
                // El endpoint del portal devuelve el shape directo (sin wrapper status/data).
                const data = (response && response.data) ? response.data : response;
                setPayload(data || null);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
                // Ante error de red el portal queda en su empty state — el fallback a V1
                // queda como decisión del padre (no auto-fallback en errores transient).
            });
    }, [isPreview, previewClientId, activeClientId]);

    // Modalidad Cards SOLO si está configurada explícitamente (CARDS_V1 — el toggle
    // es SuperAdmin-only y el default es TABLE_V2). Nunca caemos a Cards por falta de
    // plantillas ni por multi-client: un cliente TABLE_V2 sin plantillas ve un
    // empty-state del Tablero (no las Cards, que solo deben verse cuando un SuperAdmin
    // las configuró). Así el invitado y el preview muestran siempre lo mismo.
    const sets = payload?.sets || [];
    const mode = payload?.mode || 'CARDS_V1';
    const shouldFallback = !loading && mode === 'CARDS_V1';
    const hasNoTemplates = !loading && mode === 'TABLE_V2' && sets.length === 0;

    useEffect(() => {
        if (shouldFallback && onFallbackToCards) {
            onFallbackToCards();
        }
    }, [shouldFallback, onFallbackToCards]);

    // Hidratar el cache de labels de custom metrics para que las columnas FlexIndicator
    // CUSTOM:<id> muestren su display_name en vez del id raw.
    useEffect(() => {
        if (payload?.custom_metrics) {
            setCustomMetricLabelsCache(payload.custom_metrics);
        }
    }, [payload?.custom_metrics]);

    // Default activeSetId al primero apenas se cargan.
    useEffect(() => {
        if (!activeSetId && sets.length > 0) {
            setActiveSetId(sets[0].id);
        }
    }, [activeSetId, sets]);

    const activeSet = useMemo(
        () => sets.find((s) => s.id === activeSetId) || sets[0] || null,
        [sets, activeSetId]
    );

    const activeColumns = useMemo(() => activeSet?.active_columns || [], [activeSet]);

    // El `DashboardRow` no guarda los datos del BU en su state propio — los empuja al
    // parent vía `onBusinessDataFetched(buId, data)` y los recibe de vuelta como prop
    // `businessUnitData`. Si no manejamos esto en el portal, las filas quedan loading
    // para siempre (caso pre-fix: businessUnitData={null} + onBusinessDataFetched={noop}).
    const [rowsData, setRowsData] = useState({});
    const handleRowDataFetched = useCallback((buId, data) => {
        setRowsData((prev) => ({ ...prev, [buId]: data }));
    }, []);

    // ── Orden + ancho de columnas (persistido por cliente + set) ─────────────────
    const [columnOrder, setColumnOrder] = useState(null); // null = orden default del set
    const [columnWidths, setColumnWidths] = useState({});
    const [dragColId, setDragColId] = useState(null);

    const columnOrderRef = useRef(null);
    const columnWidthsRef = useRef({});
    const dragColIdRef = useRef(null);
    const isResizingRef = useRef(false);

    const clientId = payload?.agency_client_id;

    // Cargar preferencias guardadas al cambiar de set (o cliente).
    useEffect(() => {
        if (!activeSet || clientId === undefined || clientId === null) return;
        let saved = null;
        try {
            saved = JSON.parse(localStorage.getItem(colsStorageKey(clientId, activeSet.id)));
        } catch (e) {
            saved = null;
        }
        const order = saved && Array.isArray(saved.order) ? saved.order : null;
        const widths = (saved && saved.widths && typeof saved.widths === 'object') ? saved.widths : {};
        setColumnOrder(order);
        setColumnWidths(widths);
        columnOrderRef.current = order;
        columnWidthsRef.current = widths;
    }, [activeSet, clientId]);

    // Orden efectivo: respeta el guardado, filtra columnas que ya no están en el set y
    // appendea las nuevas. Si no hay guardado, usa el orden del set.
    const orderedColumns = useMemo(() => {
        if (!columnOrder) return activeColumns;
        const active = new Set(activeColumns);
        const kept = columnOrder.filter((c) => active.has(c));
        const added = activeColumns.filter((c) => !kept.includes(c));
        return [...kept, ...added];
    }, [columnOrder, activeColumns]);

    const widthOf = useCallback(
        (columnId) => Math.max(
            minColumnWidth(columnId),
            columnWidths[columnId] || defaultColumnWidth(columnId)
        ),
        [columnWidths]
    );

    const persistColumns = useCallback((order, widths) => {
        if (!activeSet || clientId === undefined || clientId === null) return;
        try {
            localStorage.setItem(colsStorageKey(clientId, activeSet.id), JSON.stringify({ order, widths }));
        } catch (e) {
            // quota / modo privado — ignorar, la personalización simplemente no persiste.
        }
    }, [activeSet, clientId]);

    // Resize: arrastrar el handle del header. Actualiza el ancho en vivo y persiste al soltar.
    const handleInitResize = useCallback((e, columnId) => {
        e.preventDefault();
        e.stopPropagation();
        isResizingRef.current = true;
        const startX = e.pageX;
        const startWidth = columnWidthsRef.current[columnId] || defaultColumnWidth(columnId);

        const onMove = (ev) => {
            const delta = ev.pageX - startX;
            const newWidth = Math.max(minColumnWidth(columnId), Math.round(startWidth + delta));
            const next = { ...columnWidthsRef.current, [columnId]: newWidth };
            columnWidthsRef.current = next;
            setColumnWidths(next);
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            persistColumns(columnOrderRef.current, columnWidthsRef.current);
            // Reset en el próximo tick: si el navegador disparó un dragstart espurio al
            // mover, ya pasó y no queremos cancelar futuros reorders.
            setTimeout(() => { isResizingRef.current = false; }, 0);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [persistColumns]);

    // Reorder: drag nativo de los headers de columnas de datos.
    const handleColDragStart = useCallback((columnId) => (e) => {
        if (isResizingRef.current) {
            e.preventDefault();
            return;
        }
        dragColIdRef.current = columnId;
        setDragColId(columnId);
        if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            try { e.dataTransfer.setData('text/plain', columnId); } catch (err) { /* guard navegadores viejos */ }
        }
    }, []);

    const handleColDragOver = useCallback((e) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    }, []);

    const handleColDrop = useCallback((targetColumnId) => (e) => {
        e.preventDefault();
        const source = dragColIdRef.current;
        dragColIdRef.current = null;
        setDragColId(null);
        if (!source || source === targetColumnId) return;

        // Base = orden efectivo actual (guardado filtrado + nuevas), garantizando todas las activas.
        const base = (columnOrderRef.current && columnOrderRef.current.length)
            ? columnOrderRef.current.filter((c) => activeColumns.includes(c))
            : [...activeColumns];
        activeColumns.forEach((c) => { if (!base.includes(c)) base.push(c); });

        const withoutSource = base.filter((c) => c !== source);
        const targetIdx = withoutSource.indexOf(targetColumnId);
        withoutSource.splice(targetIdx < 0 ? withoutSource.length : targetIdx, 0, source);

        columnOrderRef.current = withoutSource;
        setColumnOrder(withoutSource);
        persistColumns(withoutSource, columnWidthsRef.current);
    }, [activeColumns, persistColumns]);

    const handleColDragEnd = useCallback(() => {
        dragColIdRef.current = null;
        setDragColId(null);
    }, []);

    // ── Ordenamiento por columna (click en header, asc/desc) ─────────────────────
    // Reusa el mismo criterio que el Tablero general: `ordering_values` (array) por
    // columna que viene en la metric data del backend + compareByPosition/sortWithDirection.
    const [columnOrdering, setColumnOrdering] = useState(null); // ColumnWithOrdering | null

    const handleSetOrdering = useCallback((ordering) => {
        setColumnOrdering(ordering);
    }, []);

    const orderingValuesFor = useCallback((bu, columnId) => {
        if (columnId === BUSINESS_UNIT_COLUMN) {
            return [bu.name != null ? String(bu.name).toLowerCase() : ''];
        }
        const col = rowsData[bu.id]?.[columnId];
        const ov = (col && typeof col === 'object') ? col.ordering_values : null;
        return (ov != null && Array.isArray(ov)) ? ov : [Number.MIN_SAFE_INTEGER];
    }, [rowsData]);

    const sortedBusinessUnits = useMemo(() => {
        if (!columnOrdering) return businessUnits;
        const colId = columnOrdering.columnId();
        const dir = columnOrdering.orderingDirection();
        return sortWithDirection(
            [...businessUnits],
            (a, b) => compareByPosition(orderingValuesFor(a, colId), orderingValuesFor(b, colId)),
            dir
        );
    }, [businessUnits, columnOrdering, orderingValuesFor]);

    // ── Eventuales fuera de período (terminadas o todavía sin comenzar) ───────────
    // Mismo criterio que el Tablero general (`is_eventual_out_of_period`): se separan en
    // una sección colapsable para no ensuciar la vista del cliente. Colapsadas por default.
    const [showOutOfPeriod, setShowOutOfPeriod] = useState(false);
    const inPeriodBUs = useMemo(
        () => sortedBusinessUnits.filter((bu) => !bu.is_eventual_out_of_period),
        [sortedBusinessUnits]
    );
    const outOfPeriodBUs = useMemo(
        () => sortedBusinessUnits.filter((bu) => bu.is_eventual_out_of_period),
        [sortedBusinessUnits]
    );

    // Detectar overflow horizontal del scroll container para mostrar el fade-shadow
    // del borde derecho. Re-evaluamos en mount, en cambio de plantilla, resize de ventana
    // y cuando cambian los anchos de columnas.
    const scrollRef = useRef(null);
    const [hasOverflow, setHasOverflow] = useState(false);

    const recalcOverflow = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const overflowed = el.scrollWidth > el.clientWidth + 1
            && el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
        setHasOverflow(overflowed);
    }, []);

    useEffect(() => {
        recalcOverflow();
        const el = scrollRef.current;
        if (!el) return undefined;
        el.addEventListener('scroll', recalcOverflow, { passive: true });
        window.addEventListener('resize', recalcOverflow);
        return () => {
            el.removeEventListener('scroll', recalcOverflow);
            window.removeEventListener('resize', recalcOverflow);
        };
    }, [recalcOverflow, activeSetId, payload, columnWidths, orderedColumns]);

    if (loading) {
        return (
            <div className="portal-dashboard-table-loading">
                <Spinner color="positive" />
            </div>
        );
    }

    if (shouldFallback) {
        // Padre va a re-renderizar V1. Acá no renderizamos nada para evitar flash.
        return null;
    }

    if (hasNoTemplates) {
        // TABLE_V2 sin plantillas asignadas: empty-state claro en vez de degradar a
        // Cards (Cards solo debe verse si un SuperAdmin la configuró explícitamente).
        return (
            <div className="portal-dashboard-table-no-templates">
                <FaIcon iconName="table-columns" />
                <p>{t('portal.dashboard.no_templates_assigned')}</p>
            </div>
        );
    }

    // Sin líneas activas, el vacío va EN LUGAR de la tabla, con la misma forma que el de «sin
    // plantillas». Adentro de la tabla era una celda de todo el ancho (3060px) con el texto
    // centrado: quedaba fuera de la parte visible y el cliente veía encabezados sobre una franja en
    // blanco, en cualquier ancho (QA-VIS-SEGFE-27).
    if (!businessUnits || businessUnits.length === 0) {
        return (
            <div className="portal-dashboard-table-no-templates portal-dashboard-table-empty-bu">
                <FaIcon iconName="circle-info" />
                <p>{t('portal.dashboard.no_active_bus')}</p>
            </div>
        );
    }

    const totalWidth = widthOf(BUSINESS_UNIT_COLUMN)
        + orderedColumns.reduce((sum, c) => sum + widthOf(c), 0);

    // Nota: el portal se sirve de `GET /client/<id>` (BusinessUnitBasicPresenter), que expone
    // `budget_currency` — no `currency` ni `currency_conversions`. Por eso acá no se pasa
    // `currencyConversions`: para avisar de los importes convertidos en el portal habría que
    // sumarlo a ese presenter primero.
    const renderBusinessUnitRow = (bu, idx) => (
        <RowWithErrorHandler key={`${bu.id}-${activeSetId}`}>
            <DashboardRow
                rowOrderNumber={idx + 1}
                // `viewMode="client"` es además el gate de los estados por cuenta: la celda
                // (`BusinessUnitAdAccountNameCell._canReconnect`) muestra "sin conexión" / "datos
                // no actualizados" como información, pero NO el CTA de reconexión — reconectar es
                // una acción de la agencia.
                viewMode="client"
                loadClientAdAccounts={true}
                activeColumns={orderedColumns}
                filterValues={{
                    textFilter: '',
                    issuesOnly: false,
                    labelsFilter: [],
                    platformFilter: '',
                }}
                businessUnitId={bu.id}
                businessUnitName={bu.name}
                businessUnitType={bu.type}
                budgetType={bu.budget_type}
                businessUnitData={rowsData[bu.id] || null}
                syncInformation={null}
                adAccountsData={[]}
                hasAdAccountsDataToShow={false}
                channels={extractChannels(bu)}
                clientId={payload?.agency_client_id}
                clientName={''}
                downUrlsCount={0}
                hasUrls={false}
                generalAlerts={null}
                businessUnitCurrency={bu.budget_currency}
                businessUnitLabels={bu.labels || []}
                onBusinessDataFetched={handleRowDataFetched}
                onSnoozeAllAlerts={noop}
                onStopSnoozeForAllAlerts={noop}
                onLabelsChange={noop}
                agencyFlexIndicators={payload?.flex_indicators || []}
                ga4Indicators={payload?.ga4_indicators || []}
                ecommerceIndicators={payload?.ecommerce_indicators || []}
                paidMediaIndicators={payload?.paid_media_indicators || []}
                metaIndicators={payload?.meta_indicators || []}
                googleIndicators={payload?.google_indicators || []}
                tiktokIndicators={payload?.tiktok_indicators || []}
                customMetricIndicators={payload?.custom_metric_indicators || []}
            />
        </RowWithErrorHandler>
    );

    return (
        <div className="portal-dashboard-table-wrap">
            {sets.length > 1 && (
                <div className="portal-dashboard-table-selector">
                    <span className="portal-dashboard-table-selector-eyebrow">
                        {t('portal.dashboard.template_selector_label')}
                    </span>
                    <div className="portal-dashboard-table-selector-chips" role="tablist">
                        {sets.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                role="tab"
                                aria-selected={s.id === activeSetId}
                                className={`portal-dashboard-table-chip ${s.id === activeSetId ? '-active' : ''}`}
                                onClick={() => setActiveSetId(s.id)}
                            >
                                {s.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className={`portal-dashboard-table-scroll-wrap ${hasOverflow ? '-has-overflow' : ''}`}>
            <div className="portal-dashboard-table-scroll" ref={scrollRef}>
                <table
                    className="table table-striped-manually -dashboard portal-dashboard-table"
                    style={{ tableLayout: 'fixed', width: `${totalWidth}px`, minWidth: '100%' }}
                >
                    <colgroup>
                        <col style={{ width: `${widthOf(BUSINESS_UNIT_COLUMN)}px` }} />
                        {orderedColumns.map((columnId) => (
                            <col key={columnId} style={{ width: `${widthOf(columnId)}px` }} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            <ColumnHeader
                                key={BUSINESS_UNIT_COLUMN}
                                title={t('portal.dashboard.control_line_column', 'Línea de seguimiento')}
                                columnId={BUSINESS_UNIT_COLUMN}
                                isActive={columnOrdering ? columnOrdering.columnId() === BUSINESS_UNIT_COLUMN : false}
                                canBeSorted={true}
                                hasHelper={false}
                                defaultOrderingDirection={columnOrdering && columnOrdering.columnId() === BUSINESS_UNIT_COLUMN ? columnOrdering.orderingDirection() : SORT_UP}
                                onSetOrdering={handleSetOrdering}
                                onInitResize={handleInitResize}
                            />
                            {orderedColumns.map((columnId) => {
                                const columnData = getColumnData(t, columnId);
                                if (!columnData) return null;
                                return (
                                    <ColumnHeader
                                        key={columnId}
                                        title={columnData['title']}
                                        subtitle={columnData['subtitle']}
                                        helper={columnData['helper']}
                                        columnId={columnId}
                                        isActive={columnOrdering ? columnOrdering.columnId() === columnId : false}
                                        canBeSorted={true}
                                        hasHelper={Boolean(columnData['helper'])}
                                        defaultOrderingDirection={columnOrdering && columnOrdering.columnId() === columnId ? columnOrdering.orderingDirection() : SORT_DOWN}
                                        onSetOrdering={handleSetOrdering}
                                        onInitResize={handleInitResize}
                                        className={`portal-th-draggable${dragColId === columnId ? ' -dragging' : ''}`}
                                        draggable
                                        onDragStart={handleColDragStart(columnId)}
                                        onDragOver={handleColDragOver}
                                        onDrop={handleColDrop(columnId)}
                                        onDragEnd={handleColDragEnd}
                                    />
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {inPeriodBUs.map((bu, idx) => renderBusinessUnitRow(bu, idx))}
                        {outOfPeriodBUs.length > 0 && (
                            <tr className="portal-dashboard-eventual-row">
                                <td colSpan={orderedColumns.length + 1}>
                                    <button
                                        type="button"
                                        className="portal-dashboard-eventual-toggle"
                                        onClick={() => setShowOutOfPeriod((v) => !v)}
                                    >
                                        <FaIcon iconName={showOutOfPeriod ? 'chevron-down' : 'chevron-right'} />
                                        {' '}{t('portal.dashboard.eventual_out_of_period', { count: outOfPeriodBUs.length })}
                                        {' '}<span className="portal-dashboard-eventual-toggle-action">
                                            {showOutOfPeriod ? t('dashboard.hide') : t('dashboard.show')}
                                        </span>
                                    </button>
                                </td>
                            </tr>
                        )}
                        {showOutOfPeriod && outOfPeriodBUs.map(
                            (bu, idx) => renderBusinessUnitRow(bu, inPeriodBUs.length + idx)
                        )}
                    </tbody>
                </table>
            </div>
            </div>
        </div>
    );
}

// Las BUs vienen del `BusinessUnitBasicPresenter` que serializa flags
// `has_<channel>_channel`. Reconstruimos el array de channels para `DashboardRow`.
function extractChannels(bu) {
    if (Array.isArray(bu.channels)) return bu.channels;
    // Usar los VALORES de los constantes ('GOOGLE', 'DV360', ...) — no el nombre literal.
    // getChannelClassName() hace `.toLowerCase()` para armar la clase del border (`-google`,
    // `-dv360`, ...); con el literal 'GOOGLE_CHANNEL' daría `-google_channel` y no matchearía.
    const map = {
        has_google_channel: GOOGLE_CHANNEL,
        has_facebook_channel: FACEBOOK_CHANNEL,
        has_tiktok_channel: TIKTOK_CHANNEL,
        has_linkedin_channel: LINKEDIN_CHANNEL,
        has_dv360_channel: DV360_CHANNEL,
        has_mercado_ads_channel: MERCADO_ADS_CHANNEL,
        has_sheet_channel: SHEET_CHANNEL,
        has_shopify_channel: SHOPIFY_CHANNEL,
        has_tienda_nube_channel: TIENDA_NUBE_CHANNEL,
        has_woocommerce_channel: WOOCOMMERCE_CHANNEL,
        has_vtex_channel: VTEX_CHANNEL,
        has_analytics_channel: ANALYTICS_CHANNEL,
    };
    return Object.entries(map).filter(([k]) => bu[k]).map(([, v]) => v);
}
